import { FC, HTMLAttributes, KeyboardEvent } from 'react';
// Direct sibling import — importing the barrel from inside components/global
// creates a circular dependency that crashes Storybook dev (react-refresh TDZ).
import Icon from '../Icon/Primary';
import {
  Panel,
  PanelOverlay,
  ItemRow,
  ItemLabel,
  ItemTrailing,
  DealBadge,
  DealText,
  CheckmarkSlot,
} from './styles';

export interface MenuOption {
  value: string;
  label: string;
  /** Trailing discount label, e.g. "10% off". Renders with a refresh icon. */
  deal?: string;
}

export interface MenuProps extends HTMLAttributes<HTMLDivElement> {
  options: MenuOption[];
  /** Controlled selected value. */
  value?: string;
  onChange?: (value: string) => void;
}

const Menu: FC<MenuProps> = ({
  options,
  value,
  onChange,
  onKeyDown,
  ...rest
}) => {
  // Single listbox keydown handler (ARIA listbox pattern). Option keydowns
  // bubble up to the panel, so both selection and navigation live here: Enter/
  // Space selects the focused option, arrows/Home/End move roving focus.
  // preventDefault stops the page scrolling.
  const handlePanelKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    onKeyDown?.(e);
    const items = Array.from(
      e.currentTarget.querySelectorAll<HTMLElement>('[role="option"]'),
    );
    if (items.length === 0) return;
    const current = items.indexOf(document.activeElement as HTMLElement);

    if (e.key === 'Enter' || e.key === ' ') {
      const optValue =
        current === -1 ? undefined : items[current].dataset.value;
      if (optValue == null) return;
      e.preventDefault();
      onChange?.(optValue);
      return;
    }

    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(e.key)) return;
    e.preventDefault();
    let next = 0;
    if (e.key === 'End') next = items.length - 1;
    if (e.key === 'ArrowDown') next = Math.min(current + 1, items.length - 1);
    if (e.key === 'ArrowUp') next = Math.max(current - 1, 0);
    items[next].focus();
  };

  return (
    <Panel role='listbox' onKeyDown={handlePanelKeyDown} {...rest}>
      <PanelOverlay aria-hidden />
      {options.map((opt) => {
        const selected = opt.value === value;
        return (
          <ItemRow
            key={opt.value}
            role='option'
            aria-selected={selected}
            tabIndex={selected ? 0 : -1}
            data-value={opt.value}
            $selected={selected}
            onClick={() => onChange?.(opt.value)}
          >
            <ItemLabel>{opt.label}</ItemLabel>
            <ItemTrailing>
              {opt.deal != null && (
                <DealBadge>
                  <Icon name='recurring' size='md' />
                  <DealText>{opt.deal}</DealText>
                </DealBadge>
              )}
              <CheckmarkSlot $visible={selected}>
                <Icon name='checkmark' size='md' />
              </CheckmarkSlot>
            </ItemTrailing>
          </ItemRow>
        );
      })}
    </Panel>
  );
};

export default Menu;

/**
 * HOW TO IMPLEMENT - MENU
 * ---------------
 * A controlled select-list overlay panel. Renders a floating list of options
 * where the selected option shows a checkmark and a secondary background.
 * Intended to be composed inside a Dropdown trigger component.
 *
 * @example Delivery frequency picker (from Figma spec)
 * ```tsx
 * const [freq, setFreq] = useState('3-month');
 *
 * <Menu
 *   value={freq}
 *   onChange={setFreq}
 *   options={[
 *     { value: '1-month',  label: '1 Month Delivery' },
 *     { value: '3-month',  label: '3 Month Delivery', deal: '10% off' },
 *     { value: '6-month',  label: '6 Month Delivery', deal: '15% off' },
 *   ]}
 * />
 * ```
 *
 * @example With no deals (plain label list)
 * ```tsx
 * <Menu
 *   value={size}
 *   onChange={setSize}
 *   options={[
 *     { value: 'xs', label: 'Extra Small' },
 *     { value: 'sm', label: 'Small' },
 *     { value: 'md', label: 'Medium' },
 *     { value: 'lg', label: 'Large' },
 *   ]}
 * />
 * ```
 */
